// Signing the recipient list with the account key in the browser (D41, docs/07 "Signed
// recipient list"): the document and the exact bytes to sign come from ocs-proto, the
// signature from WebCrypto, so the worker's `SignedRecipientList::verify` accepts it.
import type { Device } from "../api/client";
import { messages } from "../i18n";
import { base64ToBytes, bytesToBase64 } from "../proto/bytes";
import type { SigAlg } from "../proto/frames";
import type { Proto } from "../proto/index";
import { DEVICE_KINDS, type DeviceKind, type Recipient, type RecipientList } from "../proto/recipients";
import { ulidToBytes } from "../proto/ulid";
import { fetchDevices, fetchRecipientList, putRecipientList, type RecipientListDto } from "./api";
import { type AccountKey, signWithAccountKey } from "./key";

/** A device as the API lists it, as an entry of the signed list. */
export function recipientOf(device: Device): Recipient {
  if (!DEVICE_KINDS.includes(device.kind as DeviceKind)) throw new Error(messages().account.errors.unknownKind(device.kind));
  return {
    device_id: ulidToBytes(device.id),
    kind: device.kind as DeviceKind,
    encryption_public_key: base64ToBytes(device.encryption_public_key),
    auth_public_key: { alg: device.auth_alg as SigAlg, bytes: base64ToBytes(device.auth_public_key) },
  };
}

/** Every active device (approved, unrevoked), oldest first (the API's order). */
export function activeDevices(devices: Device[]): Device[] {
  return devices.filter((d) => d.status === "active");
}

export async function signRecipientList(
  proto: Proto,
  key: AccountKey,
  list: RecipientList,
  signedAt = Date.now(),
): Promise<RecipientListDto> {
  const document = proto.recipientListDocument(list);
  const signature = await signWithAccountKey(key, proto.recipientListSigningInput(signedAt, document));
  return {
    version: list.version,
    document: bytesToBase64(document),
    signed_at: signedAt,
    signature: bytesToBase64(signature),
    sig_alg: key.alg,
  };
}

/**
 * Sign and publish the next list version over `devices` (all of one user). `version` is
 * the latest stored version plus one, or 1 for the first list.
 */
export async function publishRecipientList(proto: Proto, key: AccountKey, devices: Device[]): Promise<number> {
  const first = devices[0];
  if (!first) throw new Error(messages().account.errors.noDevices);
  const latest = await fetchRecipientList();
  const list: RecipientList = {
    user_id: ulidToBytes(first.user_id),
    version: (latest?.version ?? 0) + 1,
    recipients: devices.map(recipientOf),
  };
  await putRecipientList(await signRecipientList(proto, key, list));
  return list.version;
}

/** "Approve devices": re-sign the list with every active device the API knows. */
export async function approveDevices(proto: Proto, key: AccountKey): Promise<number> {
  return publishRecipientList(proto, key, activeDevices(await fetchDevices()));
}
